import { useEffect, useState, useRef, useCallback, useLayoutEffect } from 'react';
import { ThemesApiClient } from '../lib/themesApi';
import {
  resolveThemeById,
  tryBundledThemeForModule,
} from '../utils/resolveThemeFromBundledOrApi';

const MODULE_ID = 'aiRecording';

const isEmbeddedFromACV = (): boolean => {
  try {
    if (typeof window === 'undefined') return false;
    const byFlag = localStorage.getItem('isFromACV') === 'true';
    const byIframe = !!window.parent && window.parent !== window;
    return byFlag || byIframe;
  } catch {
    return false;
  }
};

/**
 * Convierte cualquier formato de color a RGB
 * Soporta: hex, rgb, rgba, hsl, variables CSS
 */
const colorToRgb = (color: string): { r: number; g: number; b: number } | null => {
  if (!color) return null;

  // Si es una variable CSS, obtener su valor computado
  if (color.startsWith('var(')) {
    const varMatch = color.match(/var\(([^,)]+)/);
    if (varMatch) {
      const varName = varMatch[1].trim();
      const computedValue = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
      if (computedValue) {
        color = computedValue;
      }
    }
  }

  // Parsear rgba/rgb
  const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (rgbaMatch) {
    const r = parseInt(rgbaMatch[1]);
    const g = parseInt(rgbaMatch[2]);
    const b = parseInt(rgbaMatch[3]);
    const alpha = rgbaMatch[4] ? parseFloat(rgbaMatch[4]) : 1.0;
    
    // Si la opacidad es baja, mezclar con blanco (fondo típico)
    if (alpha < 0.5) {
      return {
        r: Math.round(r * alpha + 255 * (1 - alpha)),
        g: Math.round(g * alpha + 255 * (1 - alpha)),
        b: Math.round(b * alpha + 255 * (1 - alpha))
      };
    }
    
    return { r, g, b };
  }

  // Parsear hex
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    if (hex.length === 3) {
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16)
      };
    } else if (hex.length === 6) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16)
      };
    }
  }

  // Fallback: usar computed style
  try {
    const tempDiv = document.createElement('div');
    tempDiv.style.backgroundColor = color;
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.width = '1px';
    tempDiv.style.height = '1px';
    document.body.appendChild(tempDiv);
    
    const computedColor = window.getComputedStyle(tempDiv).backgroundColor;
    document.body.removeChild(tempDiv);
    
    const computedMatch = computedColor.match(/\d+/g);
    if (computedMatch && computedMatch.length >= 3) {
      return {
        r: parseInt(computedMatch[0]),
        g: parseInt(computedMatch[1]),
        b: parseInt(computedMatch[2])
      };
    }
  } catch (e) {
    // ignore
  }

  return null;
};

function multiplyRgb(
  rgb: { r: number; g: number; b: number },
  factor: number
): { r: number; g: number; b: number } {
  return {
    r: Math.min(255, Math.max(0, Math.round(rgb.r * factor))),
    g: Math.min(255, Math.max(0, Math.round(rgb.g * factor))),
    b: Math.min(255, Math.max(0, Math.round(rgb.b * factor))),
  };
}

function rgbToCss(rgb: { r: number; g: number; b: number }): string {
  return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
}

/**
 * Texto sobre fondos hover/selected derivados del sidebar en temas playful (AIVideos).
 * getOptimalTextColor (luminancia WCAG) suele devolver blanco sobre mostaza; aquí preferimos negro como en AIVideos.
 */
function getPlayfulSidebarItemForeground(rgb: { r: number; g: number; b: number }): string {
  const lum = (rgb.r * 0.299 + rgb.g * 0.587 + rgb.b * 0.114) / 255;
  return lum > 0.55 ? '#000000' : '#ffffff';
}

/**
 * Calcula la luminancia relativa de un color (según WCAG)
 * Retorna un valor entre 0 (negro) y 1 (blanco)
 */
const getLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map(val => {
    val = val / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Calcula el contraste entre dos colores (según WCAG)
 * Retorna un valor entre 1 (sin contraste) y 21 (máximo contraste)
 */
const getContrastRatio = (color1: string, color2: string): number => {
  const rgb1 = colorToRgb(color1);
  const rgb2 = colorToRgb(color2);
  
  if (!rgb1 || !rgb2) return 1;

  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);

  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Determina el color de texto óptimo para un fondo dado
 * Retorna texto oscuro (#000000 o #1a1a1a) si el fondo es claro
 * Retorna texto claro (#ffffff o #f5f5f5) si el fondo es oscuro
 * Garantiza contraste mínimo WCAG AA (4.5:1)
 */
const getOptimalTextColor = (backgroundColor: string): string => {
  if (!backgroundColor) return '#1a1a1a';

  const rgb = colorToRgb(backgroundColor);
  if (!rgb) return '#1a1a1a';

  const luminance = getLuminance(rgb.r, rgb.g, rgb.b);
  
  // Calcular el promedio RGB para detectar fondos muy oscuros
  const avgRgb = (rgb.r + rgb.g + rgb.b) / 3;

  // Si el fondo es muy oscuro (ej. negro puro), forzar texto blanco
  if (avgRgb < 50) {
    return '#ffffff';
  }

  // Si el fondo es claro (luminancia > 0.5), usar texto oscuro
  if (luminance > 0.5) {
    const contrastBlack = getContrastRatio(backgroundColor, '#000000');
    if (contrastBlack >= 4.5) {
      return '#000000';
    }
    return '#1a1a1a';
  } else {
    // Si el fondo es oscuro (luminancia <= 0.5), usar texto claro
    const contrastWhite = getContrastRatio(backgroundColor, '#ffffff');
    if (contrastWhite >= 4.5) {
      return '#ffffff';
    }
    return '#f5f5f5';
  }
};

/**
 * Hook principal para gestionar y aplicar temas en la extensión
 * 
 * CARACTERÍSTICAS:
 * - Prioriza tema desde BD API
 * - Fallback a localStorage de ACV
 * - Fallback a localStorage propio
 * - Aplica variables CSS dinámicamente
 * - Garantiza contraste WCAG AA para temas playful
 * - Aplica principio de legibilidad dinámica
 */
export const useExtensionTheme = () => {
  const [theme, setTheme] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedInitialTheme = useRef(false);
  const themeCommandSeqRef = useRef(0);
  const themeReceivedFromParentRef = useRef(false);
  const hasAppliedLayoutCacheRef = useRef(false);
  const lastAppliedThemeSignatureRef = useRef<string | null>(null);
  /** Timestamp de la última verificación background con la API (debounce 30s). */
  const lastApiVerifyRef = useRef<number>(0);
  /** Ref estable para llamar verifyThemeWithApiInBackground desde loadTheme sin deps circulares. */
  const verifyBgRef = useRef<((id: string) => void) | null>(null);

  const getCurrentUserEmail = useCallback((): string | null => {
    try {
      const fromDirect = localStorage.getItem('userEmail');
      if (fromDirect) return fromDirect;

      const userRaw = localStorage.getItem('user');
      if (userRaw) {
        const user = JSON.parse(userRaw);
        if (typeof user?.mail === 'string') return user.mail;
        if (typeof user?.email === 'string') return user.email;
      }

      const msgRaw = localStorage.getItem('msg');
      if (msgRaw) {
        const msg = JSON.parse(msgRaw);
        if (typeof msg?.user?.mail === 'string') return msg.user.mail;
        if (typeof msg?.user?.email === 'string') return msg.user.email;
      }
    } catch {
      return null;
    }
    return null;
  }, []);

  const readCachedThemeSync = useCallback((): any | null => {
    try {
      const userEmail = getCurrentUserEmail();
      if (userEmail) {
        const byUser = localStorage.getItem(`${MODULE_ID}_theme_cache:${userEmail}`);
        if (byUser) {
          const parsedByUser = JSON.parse(byUser);
          if (parsedByUser?.id && parsedByUser?.colors) return parsedByUser;
        }
      }

      const generic = localStorage.getItem(`${MODULE_ID}_theme_cache`);
      if (generic) {
        const parsedGeneric = JSON.parse(generic);
        if (parsedGeneric?.id && parsedGeneric?.colors) return parsedGeneric;
      }
    } catch {
      return null;
    }
    return null;
  }, [getCurrentUserEmail]);

  /**
   * Aplica las variables CSS del tema al documento
   * 
   * IMPORTANTE: Esta función debe mapear TODAS las variables definidas
   * en ACV-SystemDesign.md para garantizar consistencia visual.
   */
  const applyThemeColors = useCallback((themeData: any, source: string = 'unknown') => {
    if (!themeData || !themeData.colors) {
      // console.warn('[useExtensionTheme] Invalid theme data:', themeData);
      return;
    }

    const nextSignature = `${themeData.id || 'no-id'}::${JSON.stringify(themeData.colors || {})}`;
    if (lastAppliedThemeSignatureRef.current === nextSignature && source !== 'theme_reload') {
      return;
    }
    lastAppliedThemeSignatureRef.current = nextSignature;

    const root = document.documentElement;
    const colors = themeData.colors;
    const family = themeData.family || 'conservative';

    /** ACV/shadcn tokens: `muted` = muted SURFACE; secondary TEXT is `mutedForeground` (themeColors.js). */
    const mutedBg = typeof colors.muted === 'string' && colors.muted.trim() ? colors.muted : '#f5f5f5';
    const mutedFg =
      (typeof colors.mutedForeground === 'string' && colors.mutedForeground.trim()) ||
      (typeof (colors as { muted_foreground?: string }).muted_foreground === 'string' &&
        (colors as { muted_foreground: string }).muted_foreground.trim()) ||
      '#666666';

    // console.log('[useExtensionTheme] Applying theme:', {
//       themeId: themeData.id,
//       themeName: themeData.name,
//       family,
//       source
//     });

    // Establecer atributo data-theme-family para CSS selectors
    root.setAttribute('data-theme-family', family);

    // ============================================
    // VARIABLES GLOBALES DE COMPATIBILIDAD
    // ============================================
    root.style.setProperty('--theme-background', colors.background || '#ffffff');
    root.style.setProperty('--theme-foreground', colors.foreground || '#1a1a1a');
    root.style.setProperty('--theme-foreground-secondary', mutedFg);
    root.style.setProperty('--theme-card', colors.card || '#ffffff');
    root.style.setProperty('--theme-navbar', colors.navbar || '#ffffff');
    root.style.setProperty('--theme-toolbar', colors.toolbar || colors.navbar || '#ffffff');
    root.style.setProperty('--theme-border', colors.border || '#e0e0e0');
    root.style.setProperty('--theme-primary', colors.primary || '#1976d2');
    root.style.setProperty('--theme-paper', colors.card || '#ffffff');
    root.style.setProperty('--theme-muted', mutedBg);
    root.style.setProperty('--theme-destructive', colors.error || '#f44336');

    // ============================================
    // VARIABLES PRINCIPALES (Body/Layout)
    // ============================================
    root.style.setProperty('--bg-primary', colors.background || '#ffffff');
    root.style.setProperty('--text-primary', colors.foreground || '#1a1a1a');
    root.style.setProperty('--text-secondary', mutedFg);
    root.style.setProperty('--bg-alternate', colors.card || '#f8f8f8');

    // ============================================
    // VARIABLES DE SUPERFICIES
    // ============================================
    // Para temas Playful, superficies deben usar el background del tema
    const surfaceBg = family === 'playful'
      ? (colors.background || '#ffffff')
      : (colors.card || '#ffffff');
    
    root.style.setProperty('--surface-base', surfaceBg);
    root.style.setProperty('--surface-base-foreground', colors.foreground || '#1a1a1a');
    root.style.setProperty('--surface-elevated', surfaceBg);
    root.style.setProperty('--surface-elevated-foreground', colors.foreground || '#1a1a1a');
    root.style.setProperty('--surface-hover', colors.cardHoverBg || 'rgba(0, 0, 0, 0.05)');
    root.style.setProperty('--surface-selected', colors.primary || '#1976d2');
    root.style.setProperty('--surface-selected-foreground', family === 'playful' ? '#000000' : '#ffffff');

    // ============================================
    // VARIABLES DE CARDS
    // ============================================
    // Para temas Playful, las cards también deben usar el background del tema
    // para mantener consistencia visual
    const cardBg = family === 'playful'
      ? (colors.background || '#ffffff')
      : (colors.card || '#ffffff');
    
    root.style.setProperty('--card-bg', cardBg);
    root.style.setProperty('--card-foreground', colors.foreground || '#1a1a1a');
    root.style.setProperty('--card-title-color', colors.foreground || '#1a1a1a');
    root.style.setProperty('--card-text-color', mutedFg);
    root.style.setProperty('--muted-foreground', mutedFg);
    root.style.setProperty('--mutedForeground', mutedFg);
    root.style.setProperty('--card-border', `1px solid ${colors.border || '#e0e0e0'}`);
    root.style.setProperty('--card-hover-bg', colors.cardHoverBg || 'rgba(0, 0, 0, 0.05)');
    root.style.setProperty('--card-active-bg', colors.cardActiveBg || colors.primary || '#e3f2fd');
    // Para card-active-foreground, usar el mismo color que button-primary-foreground para consistencia
    const cardActiveForeground = family === 'playful' 
      ? '#000000' 
      : (colors.buttonPrimaryForeground || '#1a1a1a');
    root.style.setProperty('--card-active-foreground', cardActiveForeground);
    root.style.setProperty('--card-selected-bg', colors.cardSelectedBg || colors.primary || '#1976d2');
    root.style.setProperty('--card-selected-foreground', family === 'playful' ? '#000000' : '#ffffff');
    root.style.setProperty('--card-selected-border', `2px solid ${colors.primary || '#1976d2'}`);

    // ============================================
    // VARIABLES DE BORDES
    // ============================================
    root.style.setProperty('--border-color', colors.border || '#e0e0e0');
    root.style.setProperty('--border-color-hover', colors.border || '#d0d0d0');
    root.style.setProperty('--border-color-focus', colors.primary || '#1976d2');
    root.style.setProperty('--border-color-error', colors.error || '#f44336');

    // ============================================
    // VARIABLES DE BOTONES
    // ============================================
    // Para temas playful, forzar texto negro para cumplir WCAG AA
    const buttonTextColor = family === 'playful' 
      ? '#000000' 
      : (colors.buttonPrimaryForeground || '#ffffff');

    // The theme colour used as text (links, outline buttons): only where it stands out from the page,
    // otherwise the text colour (e.g. Lime Burst: lime on a lime background).
    const primaryText = getContrastRatio(colors.primary || '#1976d2', colors.background || '#ffffff') >= 3
      ? (colors.primary || '#1976d2')
      : (colors.foreground || '#1a1a1a');
    root.style.setProperty('--primary-text', primaryText);

    root.style.setProperty('--button-primary-bg', colors.primary || '#1976d2');
    root.style.setProperty('--button-primary-foreground', buttonTextColor);
    root.style.setProperty('--button-primary-hover-bg', colors.primaryHover || colors.primary || '#1565c0');
    root.style.setProperty('--button-primary-active-bg', colors.primaryActive || colors.primary || '#0d47a1');
    // En temas playful, los CTA requieren borde visible para separación visual y contraste contextual.
    const primaryBorderColor =
      family === 'playful'
        ? (colors.border || colors.controlsBorder || '#1a1a1a')
        : 'transparent';
    root.style.setProperty('--button-primary-border', `1px solid ${primaryBorderColor}`);
    root.style.setProperty('--button-primary-hover-border', `1px solid ${primaryBorderColor}`);
    root.style.setProperty('--button-primary-active-border', `1px solid ${primaryBorderColor}`);
    root.style.setProperty('--button-primary-disabled-bg', '#e0e0e0');
    root.style.setProperty('--button-primary-disabled-foreground', '#999999');
    root.style.setProperty('--button-primary-border-radius', '4px');

    root.style.setProperty('--button-secondary-bg', 'transparent');
    root.style.setProperty('--button-secondary-foreground', colors.primary || '#1976d2');
    root.style.setProperty('--button-secondary-border', `1px solid ${colors.primary || '#1976d2'}`);
    root.style.setProperty('--button-secondary-hover-bg', 'rgba(0, 0, 0, 0.05)');
    root.style.setProperty('--button-secondary-active-bg', 'rgba(0, 0, 0, 0.1)');
    root.style.setProperty('--button-secondary-disabled-bg', 'transparent');
    root.style.setProperty('--button-secondary-disabled-foreground', '#999999');
    root.style.setProperty('--button-secondary-disabled-border', '#e0e0e0');

    // Botones destructivos (rojos) - para acciones peligrosas
    root.style.setProperty('--button-destructive-bg', colors.error || '#dc2626');
    root.style.setProperty('--button-destructive-foreground', '#ffffff');
    root.style.setProperty('--button-destructive-hover-bg', colors.errorHover || '#b91c1c');
    root.style.setProperty('--button-destructive-active-bg', colors.errorActive || '#991b1b');
    root.style.setProperty('--button-destructive-disabled-bg', '#e0e0e0');
    root.style.setProperty('--button-destructive-disabled-foreground', '#999999');

    root.style.setProperty('--button-focus-ring', `2px solid ${colors.primary || '#1976d2'}`);
    root.style.setProperty('--button-focus-ring-offset', '2px');
    root.style.setProperty('--button-transition', 'all 0.2s ease');

    // ============================================
    // VARIABLES DE SELECCIÓN (Recording Mode panel)
    // ============================================
    const recordingOptionBorderColor = colors.border || '#e0e0e0';
    const recordingOptionHoverBorderColor = colors.border || '#d0d0d0';
    const recordingOptionSelectedBorderColor =
      family === 'playful'
        ? (colors.foreground || colors.border || '#1a1a1a')
        : (colors.primary || '#1976d2');
    const recordingOptionSelectedBg =
      family === 'playful'
        ? (colors.cardActiveBg || colors.cardHoverBg || colors.card || colors.background || '#ffffff')
        : (colors.cardActiveBg || 'rgba(25, 118, 210, 0.1)');
    const recordingOptionSelectedFg = getOptimalTextColor(recordingOptionSelectedBg);
    const recordingOptionTextColor =
      family === 'playful'
        ? recordingOptionSelectedFg
        : (colors.foreground || '#1a1a1a');
    const recordingOptionSubtextColor =
      family === 'playful'
        ? recordingOptionSelectedFg
        : mutedFg;
    const recordingOptionSelectedShadow =
      family === 'playful'
        ? `0 0 0 2px ${recordingOptionSelectedBorderColor}`
        : 'none';

    root.style.setProperty('--recording-option-border', `1px solid ${recordingOptionBorderColor}`);
    root.style.setProperty('--recording-option-hover-border', `1px solid ${recordingOptionHoverBorderColor}`);
    root.style.setProperty('--recording-option-selected-border', `2px solid ${recordingOptionSelectedBorderColor}`);
    root.style.setProperty('--recording-option-selected-bg', recordingOptionSelectedBg);
    root.style.setProperty('--recording-option-selected-foreground', recordingOptionSelectedFg);
    root.style.setProperty('--recording-option-selected-icon', recordingOptionSelectedBorderColor);
    root.style.setProperty('--recording-option-selected-shadow', recordingOptionSelectedShadow);
    root.style.setProperty('--recording-option-text', recordingOptionTextColor);
    root.style.setProperty('--recording-option-subtext', recordingOptionSubtextColor);

    // ============================================
    // VARIABLES DE TOGGLE (Mic switch)
    // ============================================
    const toggleBorderColor =
      family === 'playful'
        ? (colors.foreground || colors.border || '#1a1a1a')
        : (colors.border || '#e0e0e0');
    const toggleOffBg =
      family === 'playful'
        ? (colors.card || colors.background || '#ffffff')
        : '#e0e0e0';
    const toggleThumbColor = family === 'playful' ? '#000000' : '#ffffff';
    const toggleThumbBorder =
      family === 'playful'
        ? `1px solid ${toggleBorderColor}`
        : '1px solid transparent';

    root.style.setProperty('--toggle-border', `1px solid ${toggleBorderColor}`);
    root.style.setProperty('--toggle-bg', toggleOffBg);
    root.style.setProperty('--toggle-bg-checked', colors.primary || '#1976d2');
    root.style.setProperty('--toggle-thumb-bg', toggleThumbColor);
    root.style.setProperty('--toggle-thumb-border', toggleThumbBorder);

    // ============================================
    // VARIABLES DE INPUTS
    // ============================================
    // Para temas Playful, inputs deben usar el background del tema
    const inputBg = family === 'playful'
      ? (colors.background || '#ffffff')
      : (colors.card || '#ffffff');
    
    // ✅ Aplicar principio de legibilidad: calcular foreground dinámicamente
    const inputFg = getOptimalTextColor(inputBg);
    root.style.setProperty('--input-bg', inputBg);
    root.style.setProperty('--input-foreground', inputFg);
    root.style.setProperty('--input-border', `1px solid ${colors.border || '#e0e0e0'}`);
    root.style.setProperty('--input-border-radius', '4px');
    
    // Calcular dinámicamente el color del placeholder
    const placeholderColor = inputFg === '#000000' || inputFg === '#1a1a1a'
      ? 'rgba(0, 0, 0, 0.5)' // Texto oscuro: placeholder gris oscuro semi-transparente
      : 'rgba(255, 255, 255, 0.6)'; // Texto claro: placeholder blanco semi-transparente
    root.style.setProperty('--input-placeholder-color', placeholderColor);
    root.style.setProperty('--input-hover-border', `1px solid ${colors.border || '#d0d0d0'}`);
    root.style.setProperty('--input-focus-border', `2px solid ${colors.primary || '#1976d2'}`);
    root.style.setProperty('--input-error-bg', colors.errorBg || '#ffebee');
    root.style.setProperty('--input-error-border', `2px solid ${colors.error || '#f44336'}`);
    root.style.setProperty('--input-success-bg', colors.successBg || '#e8f5e9');
    root.style.setProperty('--input-success-border', `2px solid ${colors.success || '#4caf50'}`);
    root.style.setProperty('--input-disabled-bg', '#f5f5f5');
    root.style.setProperty('--input-disabled-foreground', '#999999');

    // ============================================
    // VARIABLES DE DROPDOWNS
    // ============================================
    // Para temas Playful, dropdowns deben usar el background del tema
    const dropdownBg = family === 'playful'
      ? (colors.background || '#ffffff')
      : (colors.card || '#ffffff');
    const dropdownFg = getOptimalTextColor(dropdownBg);
    
    root.style.setProperty('--dropdown-bg', dropdownBg);
    root.style.setProperty('--dropdown-foreground', dropdownFg);
    root.style.setProperty('--dropdown-border', `1px solid ${colors.border || '#e0e0e0'}`);
    root.style.setProperty('--dropdown-item-bg', dropdownBg);
    root.style.setProperty('--dropdown-item-foreground', dropdownFg);
    root.style.setProperty('--dropdown-item-hover-bg', colors.cardHoverBg || 'rgba(0, 0, 0, 0.05)');
    root.style.setProperty('--dropdown-item-selected-bg', colors.primary || '#1976d2');
    // Same text as the theme's primary buttons (e.g. black on High Contrast Dark's white), not always white.
    root.style.setProperty('--dropdown-item-selected-foreground', buttonTextColor);

    // ============================================
    // VARIABLES DE NAVBAR
    // ============================================
    // Para temas Playful, navbar debe usar el mismo background que el tema
    const navbarBg = family === 'playful'
      ? (colors.background || '#ffffff')
      : (colors.navbar || colors.background || '#ffffff');
    
    root.style.setProperty('--navbar-bg', navbarBg);
    root.style.setProperty('--navbar-foreground', colors.foreground || '#1a1a1a');
    root.style.setProperty('--navbar-item-foreground', colors.foreground || '#1a1a1a');
    root.style.setProperty('--navbar-item-hover-bg', colors.cardHoverBg || 'rgba(0, 0, 0, 0.05)');
    root.style.setProperty('--navbar-item-selected-bg', colors.primary || '#1976d2');
    root.style.setProperty('--navbar-item-selected-foreground', family === 'playful' ? '#000000' : '#ffffff');

    // ============================================
    // VARIABLES DE SIDEBAR (Left Sidebar) - Según ACV-SystemDesign.md 2.11
    // ============================================
    // Para temas Playful, usar el mismo background que el tema general
    // Para otros temas, usar card o background según corresponda
    const sidebarBg = family === 'playful' 
      ? (colors.background || '#ffffff')
      : (colors.sidebarBg || colors.card || colors.background || '#ffffff');
    const sidebarFg = colors.sidebarForeground || colors.foreground || '#1a1a1a';
    const sidebarBorder = colors.sidebarBorder || colors.border || '#e0e0e0';
    
    // Variables base del sidebar
    root.style.setProperty('--sidebar-bg', sidebarBg);
    root.style.setProperty('--sidebar-foreground', sidebarFg);
    root.style.setProperty('--sidebar-border', `1px solid ${sidebarBorder}`);
    
    // Variables del header del sidebar
    root.style.setProperty('--sidebar-header-bg', colors.sidebarHeaderBg || sidebarBg);
    root.style.setProperty('--sidebar-header-foreground', colors.sidebarHeaderForeground || sidebarFg);
    root.style.setProperty('--sidebar-header-border', `1px solid ${sidebarBorder}`);
    
    // Variables de items del sidebar
    // ✅ Aplicar principio de legibilidad: calcular foreground dinámicamente
    root.style.setProperty('--sidebar-item-bg', 'transparent');

    const sidebarItemFg = getOptimalTextColor(sidebarBg);
    root.style.setProperty('--sidebar-item-foreground', sidebarItemFg);

    const sidebarRgbBase = colorToRgb(sidebarBg);

    // Selected nav item = same brand color as primary CTAs (Feedback / aiVideos / DashboardSidebar).
    // Do not darken sidebar bg (playful) or derive a "pill" from primary (non-playful).
    const sidebarSelectedBg = colors.sidebarItemSelectedBg || colors.primary || '#1976d2';

    if (family === 'playful' && sidebarRgbBase) {
      const hoverRgb = multiplyRgb(sidebarRgbBase, 0.88);
      const activeRgb = multiplyRgb(sidebarRgbBase, 0.82);
      root.style.setProperty('--sidebar-item-hover-bg', rgbToCss(hoverRgb));
      root.style.setProperty('--sidebar-item-hover-foreground', getPlayfulSidebarItemForeground(hoverRgb));
      root.style.setProperty('--sidebar-item-active-bg', rgbToCss(activeRgb));
      root.style.setProperty('--sidebar-item-active-foreground', getPlayfulSidebarItemForeground(activeRgb));
      root.style.setProperty('--sidebar-item-selected-bg', sidebarSelectedBg);
      root.style.setProperty('--sidebar-item-selected-foreground', buttonTextColor);
      root.style.setProperty(
        '--sidebar-item-hover-box-shadow',
        'inset 0 0 0 1px rgba(0, 0, 0, 0.14)'
      );
      root.style.setProperty('--sidebar-item-selected-box-shadow', 'none');
    } else {
      const sidebarItemHoverBg = colors.cardHoverBg || 'rgba(0, 0, 0, 0.05)';
      let hoverBaseBg = sidebarItemHoverBg;
      if (sidebarItemHoverBg.includes('rgba') && sidebarItemHoverBg.includes('0.05')) {
        const sidebarRgb = colorToRgb(sidebarBg);
        if (sidebarRgb) {
          const r = Math.round(sidebarRgb.r * 0.95);
          const g = Math.round(sidebarRgb.g * 0.95);
          const b = Math.round(sidebarRgb.b * 0.95);
          hoverBaseBg = `rgb(${r}, ${g}, ${b})`;
        }
      }
      const sidebarItemHoverFg = getOptimalTextColor(hoverBaseBg);
      root.style.setProperty('--sidebar-item-hover-bg', hoverBaseBg);
      root.style.setProperty('--sidebar-item-hover-foreground', sidebarItemHoverFg);

      const sidebarItemActiveBg = colors.cardActiveBg || colors.primary || '#e3f2fd';
      const sidebarItemActiveFg = getOptimalTextColor(sidebarItemActiveBg);
      root.style.setProperty('--sidebar-item-active-bg', sidebarItemActiveBg);
      root.style.setProperty('--sidebar-item-active-foreground', sidebarItemActiveFg);

      root.style.setProperty('--sidebar-item-selected-bg', sidebarSelectedBg);
      root.style.setProperty('--sidebar-item-selected-foreground', buttonTextColor);
      root.style.setProperty('--sidebar-item-hover-box-shadow', 'none');
      root.style.setProperty('--sidebar-item-selected-box-shadow', 'none');
    }
    
    // Variables de compatibilidad (mantener para componentes existentes)
    root.style.setProperty('--sidebar-background', sidebarBg);
    root.style.setProperty('--sidebar-primary', colors.primary || '#1976d2');
    root.style.setProperty('--sidebar-primary-foreground', family === 'playful' ? '#000000' : '#ffffff');
    root.style.setProperty(
      '--sidebar-accent',
      family === 'playful' && sidebarRgbBase
        ? rgbToCss(multiplyRgb(sidebarRgbBase, 0.88))
        : colors.cardHoverBg || 'rgba(0, 0, 0, 0.05)'
    );
    root.style.setProperty('--sidebar-accent-foreground', sidebarFg);
    root.style.setProperty('--sidebar-ring', colors.primary || '#1976d2');

    // ============================================
    // VARIABLES DE CONTROLS (Barra de Filtros)
    // ============================================
    // Para temas Playful, controls debe usar el mismo background que el tema
    const controlsBg = family === 'playful'
      ? (colors.background || '#ffffff')
      : (colors.controlsBg || colors.navbar || colors.card || '#ffffff');
    
    // ✅ Aplicar principio de legibilidad: calcular foreground dinámicamente
    const controlsFg = getOptimalTextColor(controlsBg);
    root.style.setProperty('--controls-bg', controlsBg);
    root.style.setProperty('--controlsBg', controlsBg);
    root.style.setProperty('--controls-foreground', controlsFg);
    root.style.setProperty('--controlsForeground', controlsFg);
    root.style.setProperty('--controls-border', colors.controlsBorder || colors.border || '#d6d6d6');
    
    const controlsInputBg = family === 'playful' ? colors.background : (colors.card || '#ffffff');
    const controlsInputFg = getOptimalTextColor(controlsInputBg);
    root.style.setProperty('--controls-input-bg', controlsInputBg);
    root.style.setProperty('--controls-input-foreground', controlsInputFg);
    root.style.setProperty('--controls-input-border', colors.border || '#d6d6d6');
    root.style.setProperty('--controls-text-color', controlsFg);

    // Variables de compatibilidad para accent-color (usadas en componentes existentes)
    root.style.setProperty('--accent-color', colors.primary || '#1976d2');
    root.style.setProperty('--accent-color-soft', colors.cardHoverBg || 'rgba(0, 0, 0, 0.05)');

    // ============================================
    // APLICAR TIPOGRAFÍA
    // ============================================
    if (themeData.typography) {
      const fontFamilyMap: { [key: string]: string } = {
        'Arial': '"Arial", sans-serif',
        'Roboto': '"Roboto", "Helvetica", "Arial", sans-serif',
        'Comic Sans': '"Comic Sans MS", cursive',
        'Serif': '"Georgia", "Times New Roman", serif',
        'Sans-Serif': '"Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
        'Monospace': '"Courier New", "Courier", monospace',
      };

      const fontFamily = fontFamilyMap[themeData.typography.fontFamily] || 
                        fontFamilyMap['Sans-Serif'];
      const fontSize = `${themeData.typography.fontSizeBase || 16}px`;

      root.style.setProperty('--font-family-base', fontFamily);
      root.style.setProperty('--font-size-base', fontSize);
      document.body.style.fontFamily = fontFamily;
      document.body.style.fontSize = fontSize;
    }

    // ============================================
    // APLICAR BACKGROUND A HTML, BODY Y ROOT
    // ============================================
    // Para temas Playful, usar el mismo background en todo
    const backgroundColor = colors.background || '#ffffff';
    const foregroundColor = colors.foreground || '#1a1a1a';
    
    // Aplicar al html
    root.style.backgroundColor = backgroundColor;
    root.style.color = foregroundColor;
    
    // Aplicar al body
    document.body.style.backgroundColor = backgroundColor;
    document.body.style.color = foregroundColor;
    
    // Aplicar al #root si existe
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.style.backgroundColor = family === 'playful' ? backgroundColor : 'transparent';
    }
    
    // Para temas Playful, también aplicar a todos los contenedores principales
    if (family === 'playful' && !isEmbeddedFromACV()) {
      // Aplicar clase CSS para que las reglas de ExtensionThemes.css funcionen
      root.setAttribute('data-theme-family', 'playful');
      
      // Aplicar background a elementos principales con un pequeño delay
      // para asegurar que el DOM esté listo
      setTimeout(() => {
        // Aplicar a contenedores principales
        const mainElements = document.querySelectorAll('main, .flex-1, .min-h-screen, [class*="bg-gray"], [class*="bg-white"], [class*="bg-slate"]');
        mainElements.forEach((el: Element) => {
          const htmlEl = el as HTMLElement;
          // Solo aplicar si no tiene un background específico ya establecido
          const computedBg = window.getComputedStyle(htmlEl).backgroundColor;
          if (computedBg === 'rgba(0, 0, 0, 0)' || 
              computedBg === 'transparent' ||
              htmlEl.classList.contains('bg-gray-50') ||
              htmlEl.classList.contains('bg-white') ||
              htmlEl.classList.contains('bg-slate-50') ||
              htmlEl.classList.contains('bg-gray-100')) {
            htmlEl.style.backgroundColor = backgroundColor;
          }
        });
        
        // Aplicar a divs principales que puedan tener backgrounds hardcodeados
        const allDivs = document.querySelectorAll('div[class*="bg-"]');
        allDivs.forEach((el: Element) => {
          const htmlEl = el as HTMLElement;
          if (htmlEl.classList.contains('bg-gray-50') ||
              htmlEl.classList.contains('bg-white') ||
              htmlEl.classList.contains('bg-slate-50') ||
              htmlEl.classList.contains('bg-gray-100')) {
            htmlEl.style.backgroundColor = backgroundColor;
          }
        });
      }, 200);
    }
  }, []);

  // Igual que Communities: aplicar caché/theme precargado antes del primer paint.
  useLayoutEffect(() => {
    if (hasAppliedLayoutCacheRef.current) return;
    hasAppliedLayoutCacheRef.current = true;

    const preloaded = (window as any).__acvPreloadedTheme;
    if (preloaded?.id && preloaded?.colors) {
      applyThemeColors(preloaded, 'cache_sync_layout');
      setTheme(preloaded);
      return;
    }

    const cached = readCachedThemeSync();
    if (cached?.id && cached?.colors) {
      applyThemeColors(cached, 'cache_sync_layout');
      setTheme(cached);
    }
  }, [applyThemeColors, readCachedThemeSync]);

  /**
   * Carga el tema con prioridad:
   * 0. Tema precargado antes de React (bootstrap)
   * 1. Bundle oficial desde moduleThemes/localStorage sync
   * 2. localStorage de ACV (moduleThemes)
   * 3. localStorage propio
   * 4. Backend API (último recurso)
   */
  const loadTheme = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const isEmbedded = isEmbeddedFromACV();
      // Si el parent ya mandó un tema vía postMessage, no sobrescribir — pero sí
      // verificar en background por si ACV envió un tema stale desde su propio localStorage.
      if (isEmbedded && themeReceivedFromParentRef.current) {
        setIsLoading(false);
        const cachedForEarlyVerify = readCachedThemeSync();
        if (cachedForEarlyVerify?.id) {
          verifyBgRef.current?.(cachedForEarlyVerify.id);
        }
        return;
      }

      const preloaded = (window as any).__acvPreloadedTheme;
      if (preloaded?.id && preloaded?.colors) {
        applyThemeColors(preloaded, 'prepaint_bootstrap');
        setTheme(preloaded);
        setIsLoading(false);
        return;
      }

      const pending = (window as any).__acvPendingThemeCommand;
      const pendingThemePayload =
        pending?.theme ||
        pending?.message?.theme ||
        pending?.payload?.theme ||
        null;
      if (pendingThemePayload?.id && pendingThemePayload?.colors) {
        applyThemeColors(pendingThemePayload, 'pending_postmessage_payload');
        setTheme(pendingThemePayload);
        setIsLoading(false);
        return;
      }
      const pendingThemeId =
        pending?.themeId ||
        pending?.message?.themeId ||
        pending?.payload?.themeId ||
        pendingThemePayload?.id;
      if (pendingThemeId && typeof pendingThemeId === 'string') {
        const pendingTheme = await resolveThemeById(pendingThemeId);
        if (pendingTheme?.id && pendingTheme?.colors) {
          applyThemeColors(pendingTheme, 'pending_postmessage_themeId');
          setTheme(pendingTheme);
          setIsLoading(false);
          return;
        }
      }

      const syncBundled = tryBundledThemeForModule(MODULE_ID);
      if (syncBundled && syncBundled.id) {
        applyThemeColors(syncBundled, 'bundled_official_sync');
        setTheme(syncBundled);
        setIsLoading(false);
        verifyBgRef.current?.(syncBundled.id as string);
        return;
      }

      // 2. Intentar desde localStorage de ACV
      const moduleThemesStr = localStorage.getItem('moduleThemes');
      if (moduleThemesStr) {
        try {
          const moduleThemes = JSON.parse(moduleThemesStr);
          const moduleTheme = moduleThemes[MODULE_ID];
          
          if (moduleTheme) {
            let themeId = null;
            if (typeof moduleTheme === 'string') {
              themeId = moduleTheme;
            } else if (moduleTheme?.themeId) {
              themeId = moduleTheme.themeId;
            }
            
            if (themeId) {
              const themeById = await resolveThemeById(themeId);
              if (themeById && themeById.id) {
                applyThemeColors(themeById, 'acv_localStorage');
                setTheme(themeById);
                setIsLoading(false);
                verifyBgRef.current?.(themeById.id as string);
                return;
              }
            }
          }
        } catch (e) {
          // console.warn('[useExtensionTheme] Error parsing moduleThemes:', e);
        }
      }

      // 3. Intentar desde localStorage propio
      const ownThemeId = localStorage.getItem(`${MODULE_ID}_themeId`);
      if (ownThemeId) {
        const themeById = await resolveThemeById(ownThemeId);
        if (themeById && themeById.id) {
          applyThemeColors(themeById, 'own_localStorage');
          setTheme(themeById);
          setIsLoading(false);
          verifyBgRef.current?.(themeById.id as string);
          return;
        }
      }

      // 4. En modo embebido ACV: NO ir a API al inicio. Esperar parent command/cache.
      // Verificar en background si el tema cacheado coincide con el de la BD.
      if (isEmbedded) {
        setIsLoading(false);
        const cachedForVerify = readCachedThemeSync();
        if (cachedForVerify?.id) {
          verifyBgRef.current?.(cachedForVerify.id);
        }
        return;
      }

      // 5. Último recurso (solo standalone): Backend API
      // console.log('[useExtensionTheme] Loading theme from API (last resort) for module:', MODULE_ID);
      const apiTheme = await ThemesApiClient.getModuleTheme(MODULE_ID);
      if (apiTheme && apiTheme.id) {
        // console.log('[useExtensionTheme] ✅ Theme loaded from API:', apiTheme.id, apiTheme.name);
        applyThemeColors(apiTheme, 'database_api_last_resort');
        setTheme(apiTheme);
        setIsLoading(false);
        return;
      }

      // 6. Fallback: sin tema
      // console.warn('[useExtensionTheme] No theme found, using default');
      setIsLoading(false);
    } catch (err: any) {
      // console.error('[useExtensionTheme] Error loading theme:', err);
      setError(err.message);
      setIsLoading(false);
    }
  }, [applyThemeColors, readCachedThemeSync]);

  const applyTheme = useCallback((themeData: any, source: string = 'manual') => {
    if (!themeData) return;
    
    applyThemeColors(themeData, source);
    setTheme(themeData);
    
    if (themeData.id) {
      localStorage.setItem(`${MODULE_ID}_themeId`, themeData.id);
      try {
        localStorage.setItem(`${MODULE_ID}_theme_cache`, JSON.stringify(themeData));
        const userEmail = getCurrentUserEmail();
        if (userEmail) {
          localStorage.setItem(`${MODULE_ID}_theme_cache:${userEmail}`, JSON.stringify(themeData));
        }
      } catch {
        // ignore cache failures (quota/private mode)
      }
    }
  }, [applyThemeColors, getCurrentUserEmail]);

  /**
   * Verifica en background si el tema activo difiere del que hay en la API (cross-device drift).
   * Si difiere, aplica el tema correcto y actualiza localStorage para futuras cargas.
   * Debounce de 30 s para no generar peticiones excesivas.
   */
  const verifyThemeWithApiInBackground = useCallback(async (appliedThemeId: string) => {
    // En modo embedded el postMessage de ACV es la fuente de verdad para correcciones.
    // Verificar contra la API de forma independiente pisaría el tema correcto del parent.
    if (isEmbeddedFromACV()) return;

    const MIN_VERIFY_INTERVAL_MS = 30_000;
    const now = Date.now();
    if (now - lastApiVerifyRef.current < MIN_VERIFY_INTERVAL_MS) return;
    lastApiVerifyRef.current = now;

    try {
      const apiTheme = await ThemesApiClient.getModuleTheme(MODULE_ID);
      if (apiTheme?.id && apiTheme.id !== appliedThemeId) {
        // console.info('[useExtensionTheme] 🔄 Cross-device drift detected:', {
//           applied: appliedThemeId,
//           correctedTo: apiTheme.id,
//           correctedName: (apiTheme as any).name
//         });
        applyTheme(apiTheme, 'api_background_verify');
      }
    } catch {
      // best-effort: ignorar fallos de red en la verificación background
    }
  }, [applyTheme]);

  // Mantener ref sincronizado para que loadTheme pueda llamarlo sin dependencia circular.
  verifyBgRef.current = verifyThemeWithApiInBackground;

  // Cargar tema al montar
  useEffect(() => {
    if (!hasLoadedInitialTheme.current) {
      hasLoadedInitialTheme.current = true;
      loadTheme();
    }
  }, [loadTheme]);

  // Escuchar comandos de tema desde postMessage
  useEffect(() => {
    const handleThemeCommand = async (event: CustomEvent) => {
      const detail = event.detail || {};
      const { type, themeId, theme: fullTheme } = detail;
      const incomingTheme =
        fullTheme ||
        detail?.message?.theme ||
        detail?.payload?.theme ||
        null;
      const incomingThemeId =
        themeId ||
        detail?.message?.themeId ||
        detail?.payload?.themeId ||
        incomingTheme?.id;
      
      if (type === 'theme-change' || type === 'APPLY_THEME') {
        // Parent envía payload completo: aplicar inmediato (sin round-trip).
        if (incomingTheme?.id && incomingTheme?.colors) {
          themeReceivedFromParentRef.current = true;
          applyTheme(incomingTheme, 'parent_command');
          return;
        }

        if (!incomingThemeId) return;
        const seq = ++themeCommandSeqRef.current;
        // console.log('[useExtensionTheme] 🎨 Theme change command received:', incomingThemeId);
        const apiTheme = await resolveThemeById(incomingThemeId);
        if (seq !== themeCommandSeqRef.current) return;
        if (apiTheme && apiTheme.id) {
          themeReceivedFromParentRef.current = true;
          applyTheme(apiTheme, 'parent_command');
          return;
        }
      } else if (type === 'theme-reload') {
        themeReceivedFromParentRef.current = false;
        hasLoadedInitialTheme.current = false;
        loadTheme();
      }
    };

    const onThemeCommand = (evt: Event) => {
      handleThemeCommand(evt as CustomEvent);
    };

    window.addEventListener('extension-theme-command', onThemeCommand);
    
    return () => {
      window.removeEventListener('extension-theme-command', onThemeCommand);
    };
  }, [applyTheme, loadTheme]);

  return {
    theme,
    isLoading,
    error,
    applyTheme,
    reloadTheme: loadTheme
  };
};

